import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Checkbox, Flex, Switch, Text, TextInput } from '@gravity-ui/uikit';
import { serverPasteParamsCheckboxes } from '../constants/pasteParamsCheckboxes';
import { ClientPasteParams, ServerPasteParams } from '../types/types';
import { PlayerRewoundEvent } from '../../../types/player/PlayerEvents';
import { formatTimeToHumanReadable } from '../../../libs/formaters/formatTimeToHumanReadable';
import { parseTimeFromString } from '../../../libs/formaters/parseTimeFromString';
import { sharePanelT } from '../../../i18n';

type PasteParamsSettingsProps = {
    serverParams: Record<ServerPasteParams, boolean>;
    setServerParams: Dispatch<SetStateAction<Record<ServerPasteParams, boolean>>>;
    clientParams: ClientPasteParams;
    setClientParams: Dispatch<SetStateAction<ClientPasteParams>>;
    isMobile?: boolean;
    onSeekTime?: (timeInSeconds: number) => void;
};

export const PasteParamsSettings: FC<PasteParamsSettingsProps> = ({
    serverParams,
    setServerParams,
    clientParams,
    setClientParams,
    isMobile,
    onSeekTime,
}) => {
    const rewoundEventHandler = (message: MessageEvent): void => {
        let playerEvent;
        let playerRewoundEvent: PlayerRewoundEvent;

        try {
            playerEvent = JSON.parse(message.data);
        } catch (e) {
            playerEvent = message.data;
        }

        if (playerEvent.event !== 'rewound') {
            return;
        }

        playerRewoundEvent = playerEvent;
        setLastRewoundTime(playerRewoundEvent.time);
        setTimeInputValue(formatTimeToHumanReadable(Math.floor(playerRewoundEvent.time)));
    };

    const [lastRewoundTime, setLastRewoundTime] = useState<number>(0);
    const [timecodeParamEnable, setTimecodeParamEnable] = useState<boolean>(false);
    const [timeInputValue, setTimeInputValue] = useState<string>('0:00');
    const [timeInputError, setTimeInputError] = useState<boolean>(false);

    useEffect(() => {
        if (timecodeParamEnable) {
            setClientParams((s) => ({ ...s, t: String(Math.floor(lastRewoundTime)) }));
        } else {
            setClientParams((params) => {
                if (params.t === undefined) {
                    return params;
                }

                const paramsWithoutTimecode = { ...params };
                delete paramsWithoutTimecode.t;
                return paramsWithoutTimecode;
            });
        }
    }, [lastRewoundTime, timecodeParamEnable, setClientParams]);

    useEffect(() => {
        window.addEventListener('message', rewoundEventHandler);
        return () => {
            window.removeEventListener('message', rewoundEventHandler);
        };
    }, []);

    const handleTimeInputChange = useCallback((value: string) => {
        setTimeInputValue(value);
        setTimeInputError(false);
    }, []);

    const handleTimeInputBlur = useCallback(() => {
        const parsedTime = parseTimeFromString(timeInputValue);

        if (parsedTime === null) {
            setTimeInputError(true);
            return;
        }

        setLastRewoundTime(parsedTime);
        setTimeInputValue(formatTimeToHumanReadable(parsedTime));
        setTimeInputError(false);

        if (onSeekTime) {
            onSeekTime(parsedTime);
        }
    }, [timeInputValue, onSeekTime]);

    const handleTimeInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleTimeInputBlur();
            }
        },
        [handleTimeInputBlur],
    );

    const handleTimecodeCheckboxUpdate = useCallback(
        (v: boolean) => {
            setTimecodeParamEnable(v);
            if (v) {
                setTimeInputValue(formatTimeToHumanReadable(Math.floor(lastRewoundTime)));
            }
        },
        [lastRewoundTime],
    );

    return (
        <>
            <Flex direction="column" gap="4">
                {serverPasteParamsCheckboxes.map(([prop, i18nKey], i) =>
                    isMobile ? (
                        <Flex key={i} justifyContent="space-between" alignItems="center" gap={3}>
                            <Text variant="body-2">{sharePanelT(i18nKey)}</Text>
                            <Switch
                                checked={serverParams[prop]}
                                onChange={(e) =>
                                    setServerParams((s) => ({ ...s, [prop]: e.target.checked }))
                                }
                            />
                        </Flex>
                    ) : (
                        <Checkbox
                            key={i}
                            content={sharePanelT(i18nKey)}
                            checked={serverParams[prop]}
                            onUpdate={(v) => setServerParams((s) => ({ ...s, [prop]: v }))}
                        />
                    ),
                )}
                {isMobile ? (
                    <Flex justifyContent="space-between" alignItems="center" gap={3}>
                        <Text variant="body-2">{sharePanelT('startFromTimecode')}</Text>
                        <Switch
                            checked={timecodeParamEnable}
                            onChange={(e) => handleTimecodeCheckboxUpdate(e.target.checked)}
                        />
                    </Flex>
                ) : (
                    <Checkbox
                        content={sharePanelT('startFromTimecode')}
                        checked={timecodeParamEnable}
                        onUpdate={handleTimecodeCheckboxUpdate}
                    />
                )}
                {timecodeParamEnable && (
                    <Flex alignItems="center" gap="2">
                        <span>{sharePanelT('startLabel')}</span>
                        <TextInput
                            value={timeInputValue}
                            onUpdate={handleTimeInputChange}
                            onBlur={handleTimeInputBlur}
                            onKeyDown={handleTimeInputKeyDown}
                            validationState={timeInputError ? 'invalid' : undefined}
                            errorMessage={sharePanelT('invalidTimeFormat')}
                            placeholder="0:00"
                            style={{ width: '120px' }}
                        />
                    </Flex>
                )}
            </Flex>
        </>
    );
};
